import { computed, observable } from "mobx";
import { FormErrors, FormStore, isField } from "@modules/formx";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import {
  ContactPersonEditor_CustomerFragment,
  ContactPersonEditor_Get,
  ContactPersonEditor_GetCustomer,
  ContactPersonEditor_Update,
  ContactPersonEditor_Extract,
  ContactPersonInput,
  ContactPersonEditor_Create,
  ContactPersonEditorFragment,
  ValidationError
} from "@graphql";
import { MutationOptions } from "@apollo/client/core/watchQueryOptions";
import {
  initialValues as formInitialValues,
  FormData,
  FormParams,
  mapDataToForm,
  formDataToInput,
  validationSchema as schema
} from "./utils";
import { YesNoCancelDialogOptions } from "@containers";

type Customer = ContactPersonEditor_CustomerFragment;

export { FormData };

export class ContactPersonFormStore {
  form: FormStore<FormData>;

  @observable allAreasOfExpertise: string[] = [];
  @observable allCustomerGroups: string[] = [];
  @observable customer: Customer;
  @observable isSubmitting = false;

  constructor(
    public readonly contactPersonId: string,
    public readonly customerId: string,
    public readonly initialValues: Partial<FormData>,
    public readonly formParams: FormParams,
    private readonly _onClose: () => void,
    private readonly mutationOptions: Partial<MutationOptions<any, any>> = {}
  ) {
    this.form = new FormStore<FormData>({
      schema,
      // debounceValidationMs: 200,
      onSubmit: this.onSubmit,
      initialValues: {
        ...formInitialValues,
        ...initialValues
      },
      validate: (store) => {
        const errors: FormErrors<FormData> = {};
        console.log("v country", this.customer, this.customer?.Address?.Country);
        // if customer country is not Danish, then areaOfExpertise is required
        if (
          this.customer?.Address?.Country !== "Denmark" &&
          (!store.values.AreasOfExpertise || store.values.AreasOfExpertise.length === 0)
        ) {
          errors.AreasOfExpertise = locale.AreasOfExpertise;
        }

        return errors;
      }
    });

    this.form.onSetValue((options) => {
      if (isField(options, "AreasOfExpertise")) {
        const added = options.value.filter((x) => !(options.prevValue || []).includes(x));
        this.addProfessionByAoe(added);
      }
    });

    this.form.loading = true;

    Promise.all([this.fetch(), this.fetchCustomer()]).finally(
      () => (this.form.loading = false)
    );

    (window as any).contactPersonFormStore = this;
  }

  @computed get allRoles() {
    return store().cache.roles.map((r) => r.Name);
  }

  @computed get allTitles() {
    return store().cache.titles;
  }

  @computed get allProfessions() {
    return store().cache.professions;
  }

  @computed get isStaffingCP() {
    if (!Array.isArray(this.form.values.Roles)) {
      return false;
    }

    return this.form.values.Roles.some(store().cache.isStaffingRole);
  }

  fetch = () => {
    if (!this.contactPersonId) {
      return Promise.resolve();
    }

    return ContactPersonEditor_Get.func(
      { id: this.contactPersonId },
      { fetchPolicy: "network-only" }
    )
      .then(({ data }) => {
        this.form.reset(mapDataToForm(data.contactPersonById));
      })
      .catch((err) => {
        store().snackbars.error(`Cannot get contact person\nReason: ${err}`);
      });
  };

  fetchCustomer = () => {
    return ContactPersonEditor_GetCustomer.func({
      customerId: this.customerId
    }).then(({ data }) => {
      this.customer = data.customerById;
      this.form.validate();
    });
  };

  onSubmit = async () => {
    const data: ContactPersonInput = formDataToInput(this.form.values);
    const id = this.contactPersonId;
    const currentCustomerId = this.customerId;

    let promise: Promise<{
      contactPerson?: ContactPersonEditorFragment;
      validationErrors: ValidationError[];
    }>;

    const processResult = async (
      _promise: Promise<{
        contactPerson?: ContactPersonEditorFragment;
        validationErrors: ValidationError[];
      } | null>
    ) => {
      _promise
        .then((result) => {
          if (!result) {
            return;
          }

          if (result.validationErrors.length > 0) {
            this.form.setErrors(result.validationErrors);
            return;
          }

          store().snackbars.success(`Contact ${result.contactPerson?.Name} saved`);
          this.form.reset(mapDataToForm(result.contactPerson));
          this._onClose();
        })
        .catch((e) => {
          store().snackbars.error(`Cannot save form. Reason: ${e}`);
        });
    };

    if (!id) {
      promise = ContactPersonEditor_Create.func({ data }, this.mutationOptions).then(
        (x) => x.data.result
      );
    } else if (this.initialValues.Customers.length > 0 && this.form.fields.Name.dirty) {
      const options = new YesNoCancelDialogOptions(locale.ContactPersonRenameConfirm);
      options.actions[1].title = locale.UpdateContactPerson;
      options.actions[2].title = locale.CreateNewContactPerson;

      promise = store()
        .ui.confirmDialog.confirm(options)
        .then((response) => {
          if (response === "cancel") {
            return null;
          }

          return response === "yes"
            ? ContactPersonEditor_Extract.func(
                {
                  contactPersonId: id,
                  customerId: currentCustomerId,
                  data
                },
                this.mutationOptions
              ).then((x) => x.data.result)
            : ContactPersonEditor_Update.func({ id, data }, this.mutationOptions).then(
                (x) => x.data.result
              );
        });
    } else {
      promise = ContactPersonEditor_Update.func({ id, data }, this.mutationOptions).then(
        (x) => x.data.result
      );
    }

    this.isSubmitting = true;

    return promise
      ? processResult(promise.finally(() => (this.isSubmitting = false)))
      : null;
  };

  close = () => {
    if (this.form.clean) {
      return this._onClose();
    }

    store().ui.confirmLeave(locale["Leave now?"], (confirmed) => {
      if (confirmed) {
        this._onClose();
      }
    });
  };

  addProfessionByAoe = (areasOfExpertise: string[]) => {
    const professions = store().cache.getProfessionsOnAreasofExp(areasOfExpertise);

    if (!this.form.values.BooksProfessions) {
      this.form.values.BooksProfessions = [];
    }

    this.form.values.BooksProfessions =
      this.form.values.BooksProfessions.concat(professions).unique();

    this.form.validate();
  };
}
